'use client'

import React, { FC, ReactElement } from 'react'
import classNames from 'classnames'
import styles from './InscriptionCard.module.css'
import Link from '@/components/link'
import { Inscription } from '@/utils/types/inscription'
import InscriptionContent from '@/components/inscription-content'
import { useTilt } from '@/components/bitmap-art'
import { useBitmapLive } from '@/components/bitmap-art/live-context'
import SetPfpButton from '@/components/set-pfp-button'
import SetSnsButton from '@/components/set-sns-button'
import { NAMES_SLUGS } from '@/utils/constants'
import formatHiddenChars from '@/utils/format-hidden-chars'
import RarityPills from '@/components/rarity-pills-blur'
import useWindowSize from '@/utils/hooks/useWindowSize'
import isFungible from '@/utils/is-fungible'
import { onKeyboardClick } from '@/utils/a11y/keyboard-click'
import useInscriptionModal from '@/utils/hooks/useInscriptionModal'
import useCollectionRanks from '@/utils/hooks/useCollectionRanks'
import numberWithCommas from '@/utils/format-number'

const InscriptionCard: FC<{
	inscription: Inscription
	footer?: React.ReactNode
	showSetPfp?: boolean
	showSetSns?: boolean
	subtitle?: string
	onClick?: (evt: any) => void
	selected?: boolean
}> = (props): ReactElement => {
	const { subtitle, onClick, selected } = props
	const { id, num, content_type, collection, meta } = props.inscription
	const [_openWarning, _setOpenWarning] = React.useState(false)
	const { isMobile } = useWindowSize()
	const openInscriptionModal = useInscriptionModal()

	const handleOpenModal = React.useCallback(
		(evt: React.MouseEvent) => {
			// Allow opening in a new tab/window via modifier keys or middle click.
			if (evt.metaKey || evt.ctrlKey || evt.shiftKey || evt.button === 1) return
			evt.preventDefault()
			evt.stopPropagation()
			openInscriptionModal({ id, inscription: props.inscription })
		},
		[id, props.inscription, openInscriptionModal]
	)

	const [_hover, setHover] = React.useState(false)

	const handleMouseEnter = React.useCallback(() => !isMobile && setHover(true), [isMobile])
	const handleMouseLeave = React.useCallback(() => !isMobile && setHover(false), [isMobile])

	const href = `/inscription/${id}`
	const name = formatHiddenChars(meta?.name as any)

	const { data: ranksData } = useCollectionRanks(collection?.slug)
	// Live bitmap tiles tilt toward the cursor instead of zooming on hover.
	const liveBitmap = useBitmapLive() && collection?.slug === 'bitmap'
	const tilt = useTilt(8)
	const rankEntry = ranksData?.ranks?.[id] || null
	const rankNumber = rankEntry ? `#${numberWithCommas(rankEntry.rank.toString())}` : null

	const tierBadge = rankEntry?.tier ? (
		<span
			className={classNames(
				'shrink-0 inline-flex items-center rounded-full px-1.5 text-[9px] leading-[14px] font-semibold whitespace-nowrap',
				rankEntry.tier.className
			)}
		>
			{rankEntry.tier.label}
		</span>
	) : null

	const rankBadge = rankNumber ? (
		<span
			className={classNames(
				'shrink-0 text-[9px] font-semibold tabular-nums whitespace-nowrap',
				rankEntry?.tier?.textClassName
			)}
		>
			Rank: {rankNumber}
		</span>
	) : null

	const nameEl = (
		<>
			<div className="flex items-center justify-between gap-1 min-w-0">
				<h3 translate="no" className="text-xs font-semibold whitespace-nowrap overflow-hidden text-ellipsis min-w-0">
					{name ? name : `Inscription #${num}`}
				</h3>
				{/* Desktop only: tier badge to the right of the name */}
				{tierBadge && <div className="hidden md:inline-flex">{tierBadge}</div>}
			</div>
			{!subtitle && (
				<div className="flex items-center justify-between gap-1 min-w-0">
					<p className="text-xs text-zinc-500 flex items-center gap-1 min-w-0 truncate">
						{name ? (num ? `#${num}` : '') : content_type}
					</p>
					{rankBadge}
				</div>
			)}
			{subtitle && (
				<div className="flex items-center justify-between gap-1 min-w-0">
					<p className="text-xs text-zinc-500 flex items-center gap-1 min-w-0 truncate">
						{subtitle}
					</p>
					{rankBadge}
				</div>
			)}
		</>
	)

	const body = (
		<>
			<div
				className={`${styles.mask} ${styles.contentWrapper} ${styles.previewMask} overflow-hidden relative`}
			>
				<div
					className={classNames(styles.previewMedia, 'transition duration-500 ease-in-out', {
						'hover:scale-110': !liveBitmap
					})}
				>
					<InscriptionContent
						showPricePer={true}
						className={`${styles.content} size-full left-0 top-0 roundex-3xl object-cover pointer-events-none inline`}
						inscription={props.inscription}
					/>
				</div>
				{content_type?.includes('image') && props.showSetPfp && (
					<SetPfpButton
						inscription={props.inscription}
						className="size-6 absolute top-2 left-2 fill-[white]"
					/>
				)}
				{NAMES_SLUGS.includes(collection?.slug as any) && props.showSetSns && (
					<SetSnsButton
						inscription={props.inscription}
						className="size-6 absolute top-2 left-2 fill-[white]"
					/>
				)}
				{!isFungible(collection?.slug as any) && (
					<RarityPills className="absolute bottom-2 right-2" inscription={props?.inscription} />
				)}
			</div>
			<div className="flex flex-col p-3">
				{onClick && (
					<a href={href} rel="noopener noreferrer" onClick={handleOpenModal} className="group">
						<div className="flex items-center justify-between gap-1 min-w-0">
							<h3 translate="no" className="text-xs font-semibold whitespace-nowrap overflow-hidden text-ellipsis group-hover:text-blue-700 dark:group-hover:text-blue-400 group-hover:underline transition-colors min-w-0">
								{name ? name : `Inscription #${num}`}
							</h3>
							{tierBadge && <div className="hidden md:inline-flex">{tierBadge}</div>}
						</div>
						{!subtitle && (
							<div className="flex items-center justify-between gap-1 min-w-0">
								<p className="text-xs text-zinc-500 flex items-center gap-1 min-w-0 truncate">
									{name ? (num ? `#${num}` : '') : content_type}
								</p>
								{rankBadge}
							</div>
						)}
						{subtitle && (
							<div className="flex items-center justify-between gap-1 min-w-0">
								<p className="text-xs text-zinc-500 flex items-center gap-1 min-w-0 truncate">
									{subtitle}
								</p>
								{rankBadge}
							</div>
						)}
					</a>
				)}
				{!onClick && <div className="flex flex-col">{nameEl}</div>}
				{props.footer && (
					<div
						className="mt-2"
						onClickCapture={
							onClick
								? (evt) => {
										// Portaled offer dialogs and native controls own their click.
										if (!evt.currentTarget.contains(evt.target as Node) ||
											(evt.target as HTMLElement).closest('button, a, input, select')) return
										evt.stopPropagation()
										onClick(evt)
								  }
								: undefined
						}
					>
						{props.footer}
					</div>
				)}
			</div>
		</>
	)

	if (onClick) {
		return (
			<div
				className={classNames(
					'group/card border hover:border-[#B692F6] cursor-pointer overflow-hidden',
					styles.cardShell,
					{
						['border-[#B692F6]']: selected,
						['border-transparent']: !selected,
						['will-change-transform active:scale-[0.97]']: liveBitmap
					}
				)}
				style={liveBitmap ? tilt.style : undefined}
				onClick={onClick}
				onMouseEnter={handleMouseEnter}
				onMouseMove={liveBitmap ? tilt.onMouseMove : undefined}
				onMouseLeave={() => {
					handleMouseLeave()
					if (liveBitmap) tilt.onMouseLeave()
				}}
				role="button"
				tabIndex={0}
				onKeyDown={onKeyboardClick(onClick)}
			>
				{body}
			</div>
		)
	}

	return (
		<Link href={href} className={styles.linkShell} onClick={handleOpenModal}>
			{body}
		</Link>
	)
}

export default InscriptionCard
